import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  IconButton,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  ThemeProvider,
  createTheme,
  CssBaseline,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  PhotoCamera as PhotoCameraIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  buscarCandidatoPorId,
  atualizarCandidato,
  uploadCurriculo,
  downloadCurriculo,
  deleteCurriculo,
  verificarCurriculo,
  uploadFoto,
  deleteFoto,
  baixarFotoObjectUrl,
} from "../../services/candidato";
import {
  normalizarNome,
  validarNome,
  formatarCpf,
  validarCpf,
} from "../../utils/normalization";

const PerfilCandidato: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  // Dados do candidato
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [novoEmail, setNovoEmail] = useState("");
  const [confirmarNovoEmail, setConfirmarNovoEmail] = useState("");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");

  // Estados para mostrar/ocultar senhas
  const [mostrarSenhaAtual, setMostrarSenhaAtual] = useState(false);
  const [mostrarNovaSenha, setMostrarNovaSenha] = useState(false);
  const [mostrarConfirmarSenha, setMostrarConfirmarSenha] = useState(false);

  // Estados de currículo e foto
  const [possuiCurriculo, setPossuiCurriculo] = useState(false);
  const [curriculoFile, setCurriculoFile] = useState<File | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string>("");
  const [fotoFile, setFotoFile] = useState<File | null>(null);

  // Diálogos
  const [openDeleteCurriculoDialog, setOpenDeleteCurriculoDialog] =
    useState(false);
  const [openDeleteFotoDialog, setOpenDeleteFotoDialog] = useState(false);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning" | "info"
  >("success");

  const showMessage = (
    message: string,
    severity: "success" | "error" | "warning" | "info" = "success",
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    if (!user?.id) {
      showMessage("Usuário não identificado", "error");
      navigate("/");
      return;
    }

    setLoading(true);
    try {
      const response = await buscarCandidatoPorId(user.id);
      const candidato = response.data;

      setNome(candidato.nome || "");
      setCpf(formatarCpf(candidato.cpf || ""));
      setTelefone(formatPhone(candidato.telefone || ""));
      setEmail(candidato.email || "");

      // Verificar se possui currículo
      try {
        const temCurriculo = await verificarCurriculo(user.id);
        setPossuiCurriculo(temCurriculo);
      } catch (error) {
        // Se der erro ao verificar currículo, assume que não tem
        setPossuiCurriculo(false);
      }

      // Carregar foto se existir (baixada via axios porque a rota exige JWT)
      try {
        const urlFoto = await baixarFotoObjectUrl(user.id);
        setFotoUrl(urlFoto || "");
      } catch (error) {
        setFotoUrl("");
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      showMessage("Erro ao carregar dados do perfil", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, "");

    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 6) {
      return numbers.replace(/^(\d{2})(\d{0,4})$/, "($1) $2");
    } else if (numbers.length <= 10) {
      // Telefone fixo: (XX) XXXX-XXXX
      return numbers.replace(/^(\d{2})(\d{4})(\d{0,4})$/, "($1) $2-$3");
    } else {
      // Celular: (XX) XXXXX-XXXX
      return numbers
        .slice(0, 11)
        .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
    }
  };

  const handleSalvar = async () => {
    if (!user?.id) {
      showMessage("Usuário não identificado", "error");
      return;
    }

    // Validações
    if (!validarNome(nome)) {
      showMessage(
        "Nome inválido! O nome não pode conter números ou caracteres especiais.",
        "error",
      );
      return;
    }

    const cpfNumerico = cpf.replace(/\D/g, "");
    if (!validarCpf(cpfNumerico)) {
      showMessage("CPF inválido!", "error");
      return;
    }

    const telefoneNumerico = telefone.replace(/\D/g, "");
    if (telefoneNumerico.length < 10) {
      showMessage("Telefone inválido!", "error");
      return;
    }

    // Validar email se estiver alterando
    if (novoEmail || confirmarNovoEmail) {
      if (novoEmail !== confirmarNovoEmail) {
        showMessage("Os e-mails não coincidem", "error");
        return;
      }

      if (novoEmail === email) {
        showMessage("O novo e-mail deve ser diferente do atual.", "error");
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(novoEmail)) {
        showMessage("Por favor, digite um e-mail válido.", "error");
        return;
      }
    }

    // Validar senha se estiver alterando
    if (novaSenha || confirmarSenha) {
      if (!senhaAtual) {
        showMessage("Digite a senha atual para alterar a senha", "error");
        return;
      }

      if (novaSenha.length < 6) {
        showMessage("A nova senha deve ter no mínimo 6 caracteres", "error");
        return;
      }

      if (novaSenha !== confirmarSenha) {
        showMessage("As senhas não coincidem", "error");
        return;
      }
    }

    setSalvando(true);
    try {
      const dadosAtualizacao: any = {
        nome: normalizarNome(nome),
        cpf: cpfNumerico,
        telefone: telefoneNumerico,
        email: novoEmail || email, // Usar novo email se preenchido
      };

      // Adicionar senha se estiver alterando
      if (novaSenha) {
        dadosAtualizacao.senha = novaSenha;
      }

      await atualizarCandidato(user.id, dadosAtualizacao);

      // Upload de currículo se houver
      if (curriculoFile) {
        await uploadCurriculo(user.id, curriculoFile);
        setPossuiCurriculo(true);
        setCurriculoFile(null);
        showMessage("Currículo enviado com sucesso!", "success");
      }

      // Upload de foto se houver
      if (fotoFile) {
        await uploadFoto(user.id, fotoFile);
        const urlFoto = await baixarFotoObjectUrl(user.id);
        setFotoUrl(urlFoto || "");
        setFotoFile(null);
        showMessage("Foto enviada com sucesso!", "success");
      }

      showMessage("Perfil atualizado com sucesso!", "success");

      // Limpar campos de email e senha
      setNovoEmail("");
      setConfirmarNovoEmail("");
      setSenhaAtual("");
      setNovaSenha("");
      setConfirmarSenha("");

      // Recarregar dados
      await carregarDados();
    } catch (error: any) {
      console.error("Erro ao salvar:", error);
      const errorMsg =
        error?.response?.data?.message || "Erro ao atualizar perfil";
      showMessage(errorMsg, "error");
    } finally {
      setSalvando(false);
    }
  };

  const handleCurriculoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        showMessage("Apenas arquivos PDF são permitidos", "error");
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        showMessage("Arquivo muito grande. Máximo 10MB", "error");
        return;
      }

      setCurriculoFile(file);
      showMessage(`Currículo selecionado: ${file.name}`, "info");
    }
  };

  const handleDownloadCurriculo = async () => {
    if (!user?.id) return;

    try {
      const blob = await downloadCurriculo(user.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `curriculo_${user.nome}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showMessage("Currículo baixado com sucesso!", "success");
    } catch (error) {
      console.error("Erro ao baixar currículo:", error);
      showMessage("Erro ao baixar currículo", "error");
    }
  };

  const handleDeleteCurriculo = async () => {
    if (!user?.id) return;

    try {
      await deleteCurriculo(user.id);
      setPossuiCurriculo(false);
      setCurriculoFile(null);
      setOpenDeleteCurriculoDialog(false);
      showMessage("Currículo removido com sucesso!", "success");
    } catch (error) {
      console.error("Erro ao remover currículo:", error);
      showMessage("Erro ao remover currículo", "error");
    }
  };

  const handleFotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        showMessage("Apenas arquivos de imagem são permitidos", "error");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        showMessage("Arquivo muito grande. Máximo 5MB", "error");
        return;
      }

      setFotoFile(file);
      // Preview da imagem
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
      showMessage(`Foto selecionada: ${file.name}`, "info");
    }
  };

  const handleDeleteFoto = async () => {
    if (!user?.id) return;

    try {
      await deleteFoto(user.id);
      setFotoUrl("");
      setFotoFile(null);
      setOpenDeleteFotoDialog(false);
      showMessage("Foto removida com sucesso!", "success");
    } catch (error) {
      console.error("Erro ao remover foto:", error);
      showMessage("Erro ao remover foto", "error");
    }
  };

  const lightTheme = createTheme({
    palette: {
      mode: "light",
      primary: {
        main: "#1976d2",
      },
      secondary: {
        main: "#dc004e",
      },
      background: {
        default: "#f5f5f5",
        paper: "#ffffff",
      },
    },
    typography: {
      fontFamily: "Montserrat, Inter, Arial, sans-serif",
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 600,
      },
      h6: {
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
          },
        },
      },
    },
  });

  if (loading) {
    return (
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5",
          }}
        >
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f5f5f5",
          py: 4,
        }}
      >
        <Container maxWidth="md">
          {/* Header com botão voltar */}
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={() => navigate("/painel-vagas")}
              sx={{
                backgroundColor: "white",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Meu Perfil
            </Typography>
          </Box>

          {/* Card de Foto */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: 3,
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Avatar
                  src={fotoUrl}
                  sx={{
                    width: 120,
                    height: 120,
                    border: "4px solid #1976d2",
                  }}
                >
                  {!fotoUrl && <PersonIcon sx={{ fontSize: 60 }} />}
                </Avatar>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="foto-upload"
                  type="file"
                  onChange={handleFotoChange}
                />
                <label htmlFor="foto-upload">
                  <IconButton
                    component="span"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      backgroundColor: "#1976d2",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "#1565c0",
                      },
                    }}
                  >
                    <PhotoCameraIcon />
                  </IconButton>
                </label>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {nome || "Sem nome"}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {email || "Sem e-mail"}
                </Typography>
                {fotoUrl && !fotoFile && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setOpenDeleteFotoDialog(true)}
                    sx={{ mt: 1 }}
                  >
                    Remover Foto
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>

          {/* Card de Currículo */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Currículo (PDF)
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <input
                  accept="application/pdf"
                  style={{ display: "none" }}
                  id="curriculo-upload"
                  type="file"
                  onChange={handleCurriculoChange}
                />
                <label htmlFor="curriculo-upload" style={{ width: "100%" }}>
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ py: 1.5 }}
                  >
                    {curriculoFile
                      ? "Alterar Currículo"
                      : possuiCurriculo
                      ? "Substituir Currículo"
                      : "Enviar Currículo"}
                  </Button>
                </label>
              </Grid>

              {possuiCurriculo && !curriculoFile && (
                <>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadCurriculo}
                      fullWidth
                      sx={{ py: 1.5 }}
                    >
                      Baixar
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setOpenDeleteCurriculoDialog(true)}
                      fullWidth
                      sx={{ py: 1.5 }}
                    >
                      Remover
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>

            {curriculoFile && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Arquivo selecionado: <strong>{curriculoFile.name}</strong> (
                {(curriculoFile.size / 1024 / 1024).toFixed(2)} MB)
                <br />
                Clique em "Salvar Alterações" para enviar
              </Alert>
            )}

            {possuiCurriculo && !curriculoFile && (
              <Chip
                label="Currículo cadastrado"
                color="success"
                icon={<VisibilityIcon />}
                sx={{ mt: 2 }}
              />
            )}
          </Paper>

          {/* Formulário de Dados */}
          <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Informações Pessoais
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nome Completo"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CPF"
                  value={cpf}
                  onChange={(e) => setCpf(formatarCpf(e.target.value))}
                  required
                  disabled
                  helperText="CPF não pode ser alterado"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Telefone"
                  value={telefone}
                  onChange={(e) => setTelefone(formatPhone(e.target.value))}
                  required
                  placeholder="(51) 99999-9999"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Seção de Alteração de Email e Senha */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexDirection: { xs: "column", md: "row" },
              mb: 3,
            }}
          >
            {/* Alterar Email */}
            <Card elevation={3} sx={{ flex: 1 }}>
              <CardHeader
                title="Alterar Email"
                avatar={<EmailIcon color="primary" />}
                titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={email}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Novo Email"
                      type="email"
                      value={novoEmail}
                      onChange={(e) => setNovoEmail(e.target.value)}
                      placeholder="Digite o novo email"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirmar Novo Email"
                      type="email"
                      value={confirmarNovoEmail}
                      onChange={(e) => setConfirmarNovoEmail(e.target.value)}
                      placeholder="Confirme o novo email"
                      error={
                        confirmarNovoEmail !== "" &&
                        novoEmail !== confirmarNovoEmail
                      }
                      helperText={
                        confirmarNovoEmail !== "" &&
                        novoEmail !== confirmarNovoEmail
                          ? "Os e-mails não coincidem"
                          : "Deixe em branco para manter o email atual"
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Alterar Senha */}
            <Card elevation={3} sx={{ flex: 1 }}>
              <CardHeader
                title="Alterar Senha"
                avatar={<LockIcon color="primary" />}
                titleTypographyProps={{ variant: "h6", fontWeight: 600 }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Senha Atual"
                      type={mostrarSenhaAtual ? "text" : "password"}
                      value={senhaAtual}
                      onChange={(e) => setSenhaAtual(e.target.value)}
                      placeholder="Digite a senha atual"
                      helperText="Necessário para alterar a senha"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setMostrarSenhaAtual(!mostrarSenhaAtual)
                              }
                              edge="end"
                            >
                              {mostrarSenhaAtual ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nova Senha"
                      type={mostrarNovaSenha ? "text" : "password"}
                      value={novaSenha}
                      onChange={(e) => setNovaSenha(e.target.value)}
                      placeholder="Digite a nova senha"
                      helperText="Mínimo 6 caracteres"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setMostrarNovaSenha(!mostrarNovaSenha)
                              }
                              edge="end"
                            >
                              {mostrarNovaSenha ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirmar Nova Senha"
                      type={mostrarConfirmarSenha ? "text" : "password"}
                      value={confirmarSenha}
                      onChange={(e) => setConfirmarSenha(e.target.value)}
                      placeholder="Confirme a nova senha"
                      error={
                        novaSenha !== "" &&
                        confirmarSenha !== "" &&
                        novaSenha !== confirmarSenha
                      }
                      helperText={
                        novaSenha !== "" &&
                        confirmarSenha !== "" &&
                        novaSenha !== confirmarSenha
                          ? "As senhas não coincidem"
                          : "Deixe em branco para manter a senha atual"
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() =>
                                setMostrarConfirmarSenha(!mostrarConfirmarSenha)
                              }
                              edge="end"
                            >
                              {mostrarConfirmarSenha ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          {/* Botão Salvar */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/painel-vagas")}
              sx={{ px: 4, py: 1.5 }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              startIcon={
                salvando ? <CircularProgress size={20} /> : <SaveIcon />
              }
              onClick={handleSalvar}
              disabled={salvando}
              sx={{ px: 4, py: 1.5 }}
            >
              {salvando ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </Box>
        </Container>

        {/* Diálogo de confirmação - Deletar Currículo */}
        <Dialog
          open={openDeleteCurriculoDialog}
          onClose={() => setOpenDeleteCurriculoDialog(false)}
        >
          <DialogTitle>Remover Currículo</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja remover seu currículo? Esta ação não pode
              ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteCurriculoDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleDeleteCurriculo} color="error" autoFocus>
              Remover
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de confirmação - Deletar Foto */}
        <Dialog
          open={openDeleteFotoDialog}
          onClose={() => setOpenDeleteFotoDialog(false)}
        >
          <DialogTitle>Remover Foto</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja remover sua foto de perfil?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteFotoDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleDeleteFoto} color="error" autoFocus>
              Remover
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default PerfilCandidato;
